// Right-side drawer that shows the cited manifesto PDF at the cited page.
// PL is one document; each PN chapter is its own PDF. Page slugs come from
// manifest-meta.json (written by scripts/extract-manifestos.ts).

import { useEffect } from 'react';
import type { CitationRef } from './Citation';
import { citationLabel } from './Citation';
import meta from '../../data/manifestos/manifest-meta.json';
import './PdfDrawer.css';

interface MetaChapter {
  chapter: string;
  pdfSlug: string;
  pageCount: number;
}

function deAccent(s: string): string {
  const map: Record<string, string> = {
    Ħ: 'h', ħ: 'h', Ġ: 'g', ġ: 'g', Ċ: 'c', ċ: 'c', Ż: 'z', ż: 'z',
    À: 'a', à: 'a', È: 'e', è: 'e', Ì: 'i', ì: 'i', Ò: 'o', ò: 'o', Ù: 'u', ù: 'u',
  };
  return s.replace(/[ĦħĠġĊċŻżÀàÈèÌìÒòÙù]/g, (c) => map[c] ?? c);
}

function chapterSlugFallback(chapter: string): string {
  return (
    'pn-' +
    deAccent(chapter)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );
}

/** Resolve a citation to a public PDF URL with a #page fragment. */
function pdfUrl(cite: CitationRef): string {
  if (cite.party === 'PL') return `/manifestos/pl.pdf#page=${cite.page}`;
  const chapters = meta.pnChapters as MetaChapter[];
  const wanted = (cite.chapter ?? '').trim().toLowerCase();
  const match = chapters.find((c) => c.chapter.trim().toLowerCase() === wanted);
  const slug = match ? match.pdfSlug : chapterSlugFallback(cite.chapter ?? 'pn');
  return `/manifestos/${slug}.pdf#page=${cite.page}`;
}

export default function PdfDrawer({
  cite,
  onClose,
}: {
  cite: CitationRef | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!cite) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll behind the drawer — without this iOS Safari lets
    // readers scroll the page through the backdrop, which feels broken.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [cite, onClose]);

  if (!cite) return null;

  const url = pdfUrl(cite);
  const heading =
    cite.party === 'PL'
      ? `PL Manifesto — page ${cite.page}`
      : `PN · ${cite.chapter ?? '?'} — page ${cite.page}`;

  return (
    <div className="mf-drawer-root" role="dialog" aria-label={heading}>
      <div className="mf-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="mf-drawer">
        <header className="mf-drawer__head">
          <span className="mf-drawer__title">{heading}</span>
          <div className="mf-drawer__actions">
            <a
              className="mf-drawer__newtab"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab ↗
            </a>
            <button
              type="button"
              className="mf-drawer__close"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </header>
        <iframe
          className="mf-drawer__frame"
          src={url}
          title={`${citationLabel(cite)} — manifesto PDF`}
        />
      </aside>
    </div>
  );
}
